<template>
  <SectionPanel>
    <template #sectionPanelContent>
      <p class="text-lg-bold text-racq-dark-grey">
        {{ policyholder.firstname?.value }} {{ policyholder.surname?.value }}
      </p>
      <p class="mb-6 text-base text-racq-med-grey">{{ title }}</p>
      <RACQFormKit
        v-if="!badResidentialAddress"
        :key="policyholderAddress"
        ref="showAddressLookupRef"
        :bindings="confirmResidentialAddress"
      />

      <FormKit type="hidden" :value="policyholder.policyHolderId" name="policyHolderId" />

      <RichText
        v-if="showAddressComponent"
        :key="subTitle?.label.value"
        class="text-racq-med-grey text-base mb-4"
        :field="subTitle?.label"
      />

      <AddressLookupOptions
        v-if="showAddressComponent"
        ref="addressEl"
        :instanceIdentifier="policyholder.policyHolderId"
        v-bind="addressLookup"
        :defaultIsOverseasOption="false"
      />
    </template>
  </SectionPanel>
</template>
<script lang="ts" setup>
import { convertSitecoreFormFieldToVueFormField } from '@/composables/useFormInputData';
import { IPolicyHolderDetails, ISitecoreFormGenericInput } from '@/interfaces';
import { computed, ref, watch } from 'vue';
import SectionPanel from '../SectionPanel/SectionPanel.vue';
import RACQFormKit from '../RACQFormKit/RACQFormKit.vue';
import AddressLookupOptions from '../AddressLookupOptions/AddressLookupOptions.vue';
import { isPOBoxAddress } from '@/utils/regex-helpers';
import { joinAddressComponentsForPolicyholder } from '@/mappers';
import { toBool } from '@/utils/convert';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { cloneDeep } from 'lodash-es';

const props = defineProps<{
  fields: ISitecoreFormGenericInput[];
  policyholder: IPolicyHolderDetails;
  index: number;
  title: string;
}>();

const showAddressLookupRef = ref<InstanceType<typeof RACQFormKit>>();
const addressEl = ref<InstanceType<typeof AddressLookupOptions>>();
const addressStore = computed(() => addressEl.value?.getStore());
const policyholderAddress = computed(() => {
  return joinAddressComponentsForPolicyholder(props.policyholder, true, false);
});
const badResidentialAddress = computed(() => {
  return isPOBoxAddress(policyholderAddress.value!);
});

const subTitle = computed(() => {
  const subTitle = cloneDeep(props.fields.find((f) => f.name == 'subTitle'));
  if (badResidentialAddress.value && subTitle?.label.value) {
    subTitle.label.value = subTitle.label.value.replace('the correct', `${props.policyholder.firstname?.value}'s'`);
  }
  return subTitle;
});

const confirmResidentialAddress = computed(() => {
  const bindings = convertSitecoreFormFieldToVueFormField(
    props.fields.find((field: ISitecoreFormGenericInput) => field.name == 'confirmResidentialAddress')!,
    'confirmResidentialAddress'
  );

  bindings.preContent = replaceBookmark('firstName', bindings.preContent!, props.policyholder.firstname?.value);
  bindings.preContent = replaceBookmark('address', bindings.preContent!, policyholderAddress.value!);
  return bindings;
});

const addressLookup = computed(() => {
  return props.fields?.find((field: ISitecoreFormGenericInput) => field.name == 'addressLookup');
});

const showAddressComponent = computed(() => {
  return (
    (showAddressLookupRef.value?.model && !toBool(showAddressLookupRef.value?.model)) || badResidentialAddress.value
  );
});

watch(addressStore, async (newVal, oldVal) => {
  if (newVal && !oldVal) {
    addressStore.value?.setupAddressComponent({
      isPostalAddress: false,
      allowOverseasAddress: true,
      showPostalSameAsInsuredAddressSelection: false,
      allowPhysicalAddressesOnly: true,
    });
  }
});
</script>
